import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { InputRuleApiModel, Country } from '~/lib/api/smartsend/types';
import { CustomerSelectionCountry } from './CustomerSelectionCountry';
import { P2GCheckBoxLegacy, P2GDropdown, P2GGrid, P2GText, P2GTextField } from '@parcel2go/core-ui';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { sanitiseKey } from '~/lib/utils/translation';
import { BooleanRuleIsOn, IsBooleanRule } from '../../lib/helpers/BooleanRuleHelper';
import { GetRuleViolation } from '../../lib/helpers/RuleValidation';

interface props {
  onUpdate?: (validation: InputRuleApiModel[]) => void;
  validation: InputRuleApiModel[];
  countries: Country[];
}

const updateValue = (validation: InputRuleApiModel[], key: string, value: string) => {
  validation.forEach((n) => {
    if (n.name === key) {
      n.value = value;
    }
  });
};
const toJsValue = (v: string) => (v == null || v == '' || v == ' ' || v == 'null' || v == undefined ? null : v);
const getValue = (validation: InputRuleApiModel[], key: string) => validation.find((v) => v.name === key)?.value;

interface ruleFieldProps {
  rule: InputRuleApiModel;
  label: string;
  onChange: (value: string) => void;
}

/**
 * A rule-driven text input that reports its own validation error.
 *
 * `P2GTextField` validates nothing itself and nothing else in this form evaluated the rule's `pattern`,
 * `minLength` or `maxLength` - so a value already wrong when the address loaded (`YO17` as a UK postcode)
 * rendered as valid and only surfaced later as an `ADDRESS-*-POSTCODE-FORMAT` issue on the item row.
 *
 * Display only: `formIsValid` in CustomerSelection still gates saving on required-and-present, so a
 * pattern the quoting API has wrong cannot trap a customer in the form.
 *
 * Errors show on load and on blur, never per keystroke - every postcode is invalid halfway through being
 * typed. Typing can only clear an error, never raise one.
 */
const RuleValidatedTextField: React.FC<ruleFieldProps> = ({ rule, label, onChange }) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const [violation, setViolation] = useState(() => GetRuleViolation(rule, rule.value));

  // What this field last wrote. Rule values are mutated in place, so `rule.value` alone cannot tell a
  // keystroke from a freshly loaded address; this can.
  const reported = useRef(rule.value);

  useEffect(() => {
    // A value from outside the field - picking a saved address - counts as a load, so validate it the way
    // the first render did.
    if (rule.value !== reported.current) {
      reported.current = rule.value;
      setViolation(GetRuleViolation(rule, rule.value));
    }
  }, [rule, rule.value]);

  const errorText = () => {
    switch (violation) {
      case 'REQUIRED':
        return t('CUSTOMER_FIELD_REQUIRED_ERROR', { field: label, defaultValue: 'Enter a {{field}}.' });
      case 'MIN-LENGTH':
        return t('CUSTOMER_FIELD_MIN_LENGTH_ERROR', { field: label, defaultValue: '{{field}} is too short.' });
      case 'MAX-LENGTH':
        return t('CUSTOMER_FIELD_MAX_LENGTH_ERROR', { field: label, defaultValue: '{{field}} is too long.' });
      case 'FORMAT':
        return t('CUSTOMER_FIELD_FORMAT_ERROR', {
          field: label,
          defaultValue: 'Enter a valid {{field}} for this service.'
        });
      default:
        return undefined;
    }
  };

  return (
    <P2GTextField
      fullWidth
      {...rule}
      id={rule.name}
      label={label}
      helperText={rule.helperText === rule.label ? '' : rule.helperText}
      error={!!violation}
      errorText={errorText()}
      onChange={(e) => {
        const value = e.currentTarget.value;
        reported.current = value;

        if (violation && !GetRuleViolation(rule, value)) {
          setViolation(undefined);
        }

        onChange(value);
      }}
      onBlur={(e) => setViolation(GetRuleViolation(rule, e.currentTarget.value))}
    />
  );
};

export const FilteredCustomerSelectionValidationInput: React.FC<props & { whitelist: string[] }> = (props) => {
  const filteredValidation = useMemo(() => {
    if (!props.validation || props.validation.length === 0) {
      return [];
    }
    return props.validation.filter((v) => props.whitelist.includes(v.name));
  }, [props.validation, props.whitelist]);

  if (!filteredValidation || filteredValidation.length === 0) {
    return null;
  }
  return (
    <CustomerSelectionValidationInput
      validation={filteredValidation}
      onUpdate={props.onUpdate}
      countries={props.countries}
    />
  );
};

export const CustomerSelectionValidationInput: React.FC<props> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const country = useMemo(() => {
    const countryIsoCode = props.validation.find((x) => x.name === 'CountryIsoCode')?.value;
    const countrySubdivision = props.validation.find((x) => x.name === 'CountrySubdivision')?.value;
    return props.countries.find(
      (x) => x.CountryIsoCode === countryIsoCode && x.CountrySubdivision === toJsValue(countrySubdivision)
    );
  }, [props.countries, props.validation]);

  const onValueUpdate = (field: string, v: string) => {
    const nv = [...props.validation];
    updateValue(nv, field, v);

    // Check this postcode isn't for a different country....
    if (field === 'Postcode') {
      const possibleSwitch = country?.Switches?.find((s) => new RegExp(s.Pattern).test(v?.toUpperCase()));
      const switchCountry =
        possibleSwitch &&
        props.countries.find(
          (c) => c.CountryIsoCode == possibleSwitch.ToIsoCode && c.CountrySubdivision == possibleSwitch.ToSubdivision
        );
      if (switchCountry) {
        updateValue(nv, 'CountryIsoCode', switchCountry.CountryIsoCode);
        updateValue(nv, 'CountrySubdivision', switchCountry.CountrySubdivision);
        if (switchCountry.States?.length > 0 && !switchCountry.States.find((s) => s.Code === getValue(nv, 'County'))) {
          updateValue(nv, 'County', switchCountry.States[0].Code);
        }
      }
    }

    props.onUpdate(nv);
  };

  const renderField = (r: InputRuleApiModel) => {
    const label = t(sanitiseKey(`CUSTOMER_${r.name}_LABEL`), { defaultValue: r.label });

    // Gated on the rule's type, not its name, so any boolean rule the quoting API sends renders as a
    // checkbox without a per-field code change.
    if (IsBooleanRule(r)) {
      return (
        <P2GGrid item xs={12} key={r.name}>
          {/* Legacy variant deliberately: P2GCheckBox has no controlled-checked prop (it drops `value`),
              whereas this one takes `checked`. Same choice actions/Extras.tsx already makes. */}
          <P2GCheckBoxLegacy
            id={r.name}
            dataTestId={`chk-${r.name}`}
            labelPlacement="end"
            label={<P2GText variant="body2">{label}</P2GText>}
            checked={BooleanRuleIsOn(r.value, r)}
            onChange={(checked) => onValueUpdate(r.name, checked ? 'true' : 'false')}
          />
        </P2GGrid>
      );
    }

    switch (r.name) {
      case 'CountryIsoCode':
      case 'CountrySubdivision':
        return null;
      case 'County':
        return (
          <P2GGrid item xs={6} key={r.name}>
            {country?.States?.length > 0 ? (
              <P2GDropdown
                fullWidth
                {...r}
                required={false}
                options={
                  country?.States.map((x) => ({ value: x.Code, text: x.Name, key: `${x.Code}_${x.Name}` })) || []
                }
                label={label}
                key={r.name}
                id={r.name}
                onChange={(v: string) => onValueUpdate(r.name, v)}
              />
            ) : (
              <RuleValidatedTextField key={r.name} rule={r} label={label} onChange={(v) => onValueUpdate(r.name, v)} />
            )}
          </P2GGrid>
        );
      default:
        return (
          <P2GGrid item xs={['Name', 'Email', 'Phone', 'Organisation'].includes(r.name) ? 12 : 6} key={r.name}>
            <RuleValidatedTextField key={r.name} rule={r} label={label} onChange={(v) => onValueUpdate(r.name, v)} />
          </P2GGrid>
        );
    }
  };

  if (!props.validation || props.validation.length === 0) {
    return null;
  }

  return (
    <P2GGrid container spacing={2}>
      <P2GGrid item xs={12}>
        <CustomerSelectionCountry country={country} />
      </P2GGrid>
      {props.validation.map((r) => (
        <Fragment key={`r_${r.name}`}>{renderField(r)}</Fragment>
      ))}
    </P2GGrid>
  );
};
